import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Star, ArrowRight, Users } from 'lucide-react';
import { features, stats } from '../data/mock';

const AboutSection = () => {
  return (
    <section className="py-28 bg-white relative overflow-hidden">
      {/* Soft brand ambient — navy + coral */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 90% 10%, rgba(255,78,80,0.06), transparent 50%),' +
            'radial-gradient(ellipse at 10% 90%, rgba(10,42,99,0.07), transparent 50%)',
        }}
      />
      <div className="container-pn relative grid md:grid-cols-12 gap-14 items-center">
        {/* Left: Visual */}
        <div className="md:col-span-6 relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1503694978374-8a2fa686963a"
              alt="Industrial printing"
              className="w-full h-[380px] sm:h-[460px] md:h-[560px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#050F2E]/60 via-transparent to-transparent" />
          </div>

          {/* Floating stat 1 */}
          <div className="absolute top-4 right-4 md:-top-8 md:-right-6 bg-white rounded-2xl shadow-2xl p-5 md:p-6 border border-slate-100 w-44 md:w-60">
            <p className="text-4xl md:text-5xl font-black text-[#0A2A63]">+{stats.customers}</p>
            <p className="text-xs md:text-sm text-slate-500 mt-1 font-semibold">Happy Customers</p>
          </div>

          {/* Floating stat 2 */}
          <div className="absolute bottom-4 left-4 md:-bottom-8 md:-left-6 bg-[#0A2A63] text-white rounded-2xl shadow-2xl p-5 md:p-6 w-52 md:w-64 overflow-hidden">
            {/* Brand color strip at top of card */}
            <div className="absolute top-0 left-0 right-0 h-1.5 flex">
              {['#0A2A63', '#1E4CB8', '#4A76D6', '#FF4E50', '#E03B3D'].map((c) => (
                <span key={c} className="flex-1" style={{ backgroundColor: c }} />
              ))}
            </div>
            <Link to="/testimonials" className="block relative">
              <div className="flex items-center gap-1 mb-2">
                {[1,2,3,4,5].map(i => <Star key={i} className="h-4 w-4 fill-[#FF4E50] text-[#FF4E50]" />)}
              </div>
              <p className="text-xl font-black">5.0 Rating</p>
              <p className="text-xs text-slate-300 mt-1">Read all testimonials →</p>
            </Link>
          </div>
        </div>

        {/* Right: Copy */}
        <div className="md:col-span-6">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">About Us</p>
          <h2 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight leading-tight">
            Creating quality and reliable prints, <span className="text-[#0A2A63]/70 italic font-serif">let&apos;s go together?</span>
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            With a combined +100 years involved in many aspects of the printing industry, ProofNation was conceived to bring you proofing and wide-format solutions that are industry-leading products.
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-4">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#FF4E50]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="h-4 w-4 text-[#FF4E50]" />
                </span>
                <span className="text-slate-700 font-medium">{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/contact">
              <Button className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 px-7 font-bold btn-transition group" data-testid="about-talk-specialist">
                Talk to a specialist
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/about">
              <Button
                variant="outline"
                className="rounded-full h-12 px-7 font-bold border-[#0A2A63]/20 text-[#0A2A63] hover:bg-[#0A2A63]/5 hover:text-[#0A2A63] btn-transition group bg-transparent"
                data-testid="about-meet-team"
              >
                <Users className="h-4 w-4 mr-2" />
                Meet the team
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
